import { OrgPage } from "@client/components/round/allocator/EvaluablePage/OrgPage";
import { RoundNavbar } from "@client/components/round/RoundNavbar";
import LoadingSpinner from "@client/components/utils/LoadingSpinner";
import Modal from "@client/components/utils/Modal";
import { useIsOutOfDate } from "@client/components/utils/OutOfDateVersionWarning";
import { ComparisonsPage } from "@client/pages/round/analysis/ComparisonsPage";
import { DisagreementsPage } from "@client/pages/round/analysis/DisagreementsPage";
import { trpc } from "@client/trpc";
import { cyrb53 } from "@client/utils/crb53";
import { Client } from "@engine/Client";
import { ActionMessage, roundReducer } from "@engine/core";
import {
  getActiveEvaluators,
  getEvaluatorShortname,
  getNodeByShortname,
  getUserShortname,
  isUserFunder,
} from "@engine/round/accessors";
import { Funder, RoundAggregate } from "@engine/round/aggregate";
import SPAStorageAdapterWrapper from "@engine/storageAdapters/SPAStorageAdapterWrapper";
import { getLogoutLink } from "@lib/auth/client";
import Container from "@lib/auth/client/Login/Container";
import Layout from "@lib/auth/client/Login/Layout";
import AuthNavbar from "@lib/auth/client/Login/LoginNavbar";
import { trackNames } from "@lib/types";
import { generateClientId } from "@lib/utils/idGeneration";
import { sleep } from "@lib/utils/sleep";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { type Id, toast } from "react-toastify";
import { Redirect, Route, Switch, useSearchParams } from "wouter";
import {
  Dispatch,
  RoundData,
  State,
  useEventSourcing,
} from "../../ImperativeState";
import DataSyncing from "../admin/DataSyncing";
import ProposalForms from "../admin/ProposalForms";
import RoundMembers from "../admin/RoundMembers";
import TrackSettings from "../admin/TrackSettings";
import { Aggregator } from "./allocator/Aggregator";
import { EvaluatorSubpage } from "./allocator/EvaluatorSubpage";
import { ExportData } from "./ExportData";
import Investigations from "./Investigations";
import { Logs } from "./logs/Logs";
import { ShowEventLogItem } from "./logs/ShowEventLogItem";
import Onboarding from "./Onboarding";
import Recusals from "./Recusals";
import Settings from "./Settings.js";
import { ProposalsPage } from "./settings/Proposals";
import { SpeculationGrants } from "./SpeculationGrants";

export const StateContext = createContext<State>({} as State);
export const DispatchContext = createContext<Dispatch>(
  (() => new Promise(() => {})) as Dispatch,
);

const getSimulationHorizonFromLocalStorage = (
  round: RoundAggregate,
): number => {
  const storedValue = localStorage.getItem("simulationHorizon");
  return storedValue !== null
    ? Number(storedValue)
    : getStartingSimulationHorizon(round);
};

const setSimulationHorizonToLocalStorage = (value: number) =>
  localStorage.setItem("simulationHorizon", String(value));

export const ClientSettingsContext = createContext<{
  getSimulationHorizon: (round: RoundAggregate) => number;
  setSimulationHorizon: (n: number) => void;
}>({
  getSimulationHorizon: (round) => getSimulationHorizonFromLocalStorage(round),
  setSimulationHorizon: (number) => setSimulationHorizonToLocalStorage(number),
});

function handleError(e: string) {
  toast.error(e);
  // console.log("client got error ", e);
}

async function downloadRound(roundId: string): Promise<RoundAggregate> {
  const result = await trpc().getAggregate.query({ roundId });
  if (result === "round-member-not-found") {
    if (typeof window !== "undefined") {
      window.location.href = getLogoutLink();
    }
    throw new Error("User is not a member of this round");
  }
  return result as RoundAggregate;
}

async function downloadActionsById(
  roundId: string,
  ids: string[],
): Promise<{ [id: string]: ActionMessage }> {
  const result = await trpc().getAggregateActionsById.query({ roundId, ids });
  if (result === "round-member-not-found") {
    if (typeof window !== "undefined") {
      window.location.href = getLogoutLink();
    }
    throw new Error("User is not a member of this round");
  }
  return result;
}

async function loadRoundData(
  clientId: string,
  roundId: string,
  client: Client | null,
): Promise<RoundData> {
  const userId = await trpc().getCurrentUserId.query();
  if (!userId) throw new Error("Must be logged in");
  const userProfiles = await trpc().getUserProfiles.query({ roundId });
  const userProfile = userProfiles[userId];
  if (!userProfile) {
    throw new Error("User not found in user profiles for round");
  }

  const userProfileHash = cyrb53(JSON.stringify(userProfiles)).toString();

  const storage = await SPAStorageAdapterWrapper.init(
    roundReducer,
    roundId,
    userProfileHash,
    downloadRound,
    downloadActionsById,
    async (aggregateId, versions) => {
      if (aggregateId !== roundId) {
        throw new Error("Invalid aggregate ID");
      }
      const result = await trpc().getAggregateActionsByVersion.query({
        roundId,
        versions,
      });
      if (result === "round-member-not-found") {
        if (typeof window !== "undefined") {
          window.location.href = getLogoutLink();
        }
        throw new Error("User is not a member of this round");
      }
      return result;
    },
  );

  if (client == null || client.id !== clientId) {
    client = new Client({
      id: clientId,
      userId,
      aggregateId: roundId,
      onErrorCallback: handleError,
      reducer: roundReducer,
      storage,
    });
  }

  return { client, userProfiles };
}

export function Round({ params }: { params: { roundId: string } }) {
  const [roundData, setRoundData] = useState<RoundData | null>(null);
  const [error, setError] = useState("");
  const clientIdRef = useRef(generateClientId());
  const clientId = clientIdRef.current;
  const [refetchCount, setRefetchCount] = useState(0);
  const clientRef = useRef<Client | null>(null);

  useEffect(() => {
    void loadRoundData(clientId, params.roundId, clientRef.current)
      .then((data) => {
        setRoundData(data);
        clientRef.current = data.client;
      })
      .catch((err) => {
        setError("Failed to load round data. Please try again.");
        console.error("Error loading round:", err);
      });
  }, [params.roundId, clientId, refetchCount]);

  const refetchUserProfiles = useCallback(() => {
    setRefetchCount((prev) => prev + 1);
  }, []);

  if (!roundData) {
    return (
      <Layout>
        <AuthNavbar />
        <main>
          <Container>
            {error ? (
              error
            ) : (
              <div className="flex flex-col items-center">
                <p className="pb-8">Warning: first load may take up to 60s</p>
                <LoadingSpinner />
              </div>
            )}
          </Container>
        </main>
      </Layout>
    );
  }

  return (
    <RoundWrapper
      roundData={roundData}
      roundId={params.roundId}
      refetchUserProfiles={refetchUserProfiles}
    />
  );
}

export function getStartingSimulationHorizon(round: RoundAggregate) {
  const funders = Object.values(round.members).flatMap((member) =>
    Object.values(member.evaluators).filter(
      (evaluator) =>
        evaluator.type === "funder" && evaluator.status === "active",
    ),
  ) as Funder[];

  let totalBudget = 0;

  for (const funder of funders) {
    totalBudget += funder.budget;
  }

  return totalBudget;
}

export const PinnedNodesContext = createContext<{
  pinnedNodes: string[];
  hoveredNode: string | null;
  pinNode: (node: string) => void;
  unpinNode: (node: string) => void;
  togglePinNode: (node: string) => void;
  setHoveredNode: (node: string | null) => void;
}>({
  pinnedNodes: [],
  hoveredNode: null,
  pinNode: () => {},
  unpinNode: () => {},
  togglePinNode: () => {},
  setHoveredNode: () => {},
});

export function useEvaluableControls(
  nodeId: string,
  relatedShortNames?: string[],
) {
  const context = useContext(PinnedNodesContext);

  const isSourceHighlighted =
    !!relatedShortNames &&
    !!context.hoveredNode &&
    relatedShortNames.includes(context.hoveredNode);

  const isFocus =
    context.pinnedNodes.includes(nodeId) ||
    context.hoveredNode === nodeId ||
    isSourceHighlighted;

  const hover = () => context.setHoveredNode(nodeId);
  const unhover = () => context.setHoveredNode(null);

  return { isFocus, hover, unhover };
}

export const RefetchUserProfilesContext = createContext<() => void>(() => {});

function BasePathRedirector({ roundId }: { roundId: string }) {
  const { round, actingAsUserId: userId } = useContext(StateContext);

  if (!userId || !roundId) return null; // Still loading or user/round ID not found

  const roundMember = round.members[userId];
  const memberTrack = roundMember?.track;

  const trackStatus =
    memberTrack != null ? round.trackSettings[memberTrack]?.status : undefined;

  // admin should go to settings, but funder probably wants to go to sankey diagram or their own evaluations

  if (isUserFunder(round, userId)) {
    if (
      Object.entries(round.trackSettings).some(
        ([_, v]) => v.status !== "1.onboarding",
      )
    ) {
      return <Redirect to={`/allocations/aggregator`} />;
    } else {
      return <Redirect to={`/onboarding`} />;
    }
  }

  if (roundMember.isRoundAdmin || memberTrack === "NO_TRACK") {
    return <Redirect to={`/settings`} />;
  }

  if (trackStatus === "1.onboarding") {
    return <Redirect to={`/onboarding`} />;
  } else if (
    trackStatus === "2.initial-evaluations" ||
    trackStatus === "3.full-discussion"
  ) {
    const evaluator = getActiveEvaluators(round).find(
      (evaluator) => evaluator.memberId === userId,
    );
    if (evaluator) {
      const evaluatorShortname = getEvaluatorShortname(round, evaluator.id);
      return <Redirect to={`/allocations/${evaluatorShortname}`} />;
    } else {
      return <Redirect to="/investigations" />;
    }
  } else if (trackStatus === "4.evals-freeze" || trackStatus === "5.complete") {
    return <Redirect to={`/allocations/aggregator`} />;
  }

  return null;
}

function RoundWrapper({
  roundData,
  roundId,
  refetchUserProfiles,
}: {
  roundData: RoundData;
  roundId: string;
  refetchUserProfiles: () => void;
}) {
  const [state, dispatch] = useEventSourcing(roundData);
  const undoToastIds = useRef<{ [actionId: string]: Id }>({});
  const [searchParams, setSearchParams] = useSearchParams();
  const { engineIsOutOfDate } = useIsOutOfDate();

  const [simulationHorizon, setSimulationHorizonState] = useState(() => {
    return getSimulationHorizonFromLocalStorage(state.round);
  });

  const proposalInModalId = searchParams.get("proposal");

  const setSimulationHorizon = (value: number) => {
    setSimulationHorizonState(value);
    setSimulationHorizonToLocalStorage(value);
  };

  useEffect(() => {
    if (engineIsOutOfDate) {
      state.disconnect();
    }
  }, [engineIsOutOfDate, state]);

  useEffect(() => {
    const isMac = navigator.platform.includes("Mac");
    const undoKey = isMac ? "⌘Z" : "Ctrl+Z";
    const redoKey = isMac ? "⌘⇧Z" : "Ctrl+Shift+Z";

    async function handleUndo() {
      const result = await roundData.client.dispatchUndo();
      if (result instanceof Error) {
        console.error("Undo failed", result);
        toast.error(
          <div>
            <div>Undo ({undoKey}) failed</div>
          </div>,
        );
      } else if (result === null) {
        return; // No undoable actions -- don't show a message or take any action
      } else {
        const { message, originalAction } = result;
        const toastId = toast.success(
          <div>
            <div>Undo ({undoKey}) of event successful:</div>
            {originalAction && (
              <div>
                <ShowEventLogItem round={state.round} item={originalAction} />
              </div>
            )}
            <div>
              <button
                className="bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white cursor-pointer p-2 rounded-md"
                onClick={handleRedo}
              >
                Redo ({redoKey})
              </button>
            </div>
          </div>,
          {
            onClose: () => {
              delete undoToastIds.current[message.id];
            },
          },
        );
        undoToastIds.current[message.id] = toastId;
      }
    }

    async function handleRedo() {
      const result = await roundData.client.dispatchRedo();
      if (result instanceof Error) {
        toast.error(
          <div>
            <div>Redo ({redoKey}) failed</div>
            <div>{result.message}</div>
          </div>,
        );
      } else if (result === null) {
        return; // No redoable actions -- don't show a message or take any action
      } else {
        const { message, originalAction } = result;
        const originalActionSummary = originalAction ? (
          <div>
            <ShowEventLogItem round={state.round} item={originalAction} />
          </div>
        ) : null;
        if (message.redoes != null && message.undoes != null) {
          toast.success(
            <div>
              <div>Redo ({redoKey}) of event successful:</div>
              {originalActionSummary}
            </div>,
          );
          const undoActionId = message.undoes;
          if (undoToastIds.current[undoActionId]) {
            const toastId = undoToastIds.current[undoActionId];
            toast.update(toastId, {
              render: (
                <div>
                  <div>Undo ({undoKey}) of event successful:</div>
                  {originalActionSummary}
                </div>
              ),
            });
          }
        }
      }
    }

    async function keydownHandler(e: KeyboardEvent) {
      const isTriggered = e.key === "z" && (e.ctrlKey || e.metaKey);
      if (!isTriggered) {
        return;
      }

      e.preventDefault();

      if (e.shiftKey) {
        await handleRedo();
      } else {
        await handleUndo();
      }
    }

    document.addEventListener("keydown", keydownHandler);
    return () => {
      document.removeEventListener("keydown", keydownHandler);
    };
  }, [roundData.client, state.round]);

  const [pinnedNodes, setPinnedNodes] = useState<string[]>([]);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  function pinNode(node: string) {
    setPinnedNodes((prev) => [...prev, node]);
  }

  function unpinNode(node: string) {
    setPinnedNodes((prev) => prev.filter((n) => n !== node));
  }

  function togglePinNode(node: string) {
    setPinnedNodes((prev) =>
      prev.includes(node) ? prev.filter((n) => n !== node) : [...prev, node],
    );
  }

  return (
    <RefetchUserProfilesContext.Provider value={refetchUserProfiles}>
      <StateContext.Provider value={state}>
        <DispatchContext.Provider value={dispatch}>
          <ClientSettingsContext.Provider
            value={{
              getSimulationHorizon: () => simulationHorizon,
              setSimulationHorizon: setSimulationHorizon,
            }}
          >
            <PinnedNodesContext.Provider
              value={{
                pinnedNodes,
                pinNode,
                unpinNode,
                togglePinNode,
                hoveredNode,
                setHoveredNode,
              }}
            >
              <div className="flex flex-col w-screen h-screen">
                <Banner />
                <div className="flex flex-1 overflow-hidden">
                  <RoundNavbar roundId={roundId} />
                  <Switch>
                    <Route path="/">
                      {() => <BasePathRedirector roundId={roundId} />}
                    </Route>
                    <Route path="/settings/users">
                      {() => (
                        <Redirect
                          to={`/round/${roundId}/admin/round-members`}
                        />
                      )}
                    </Route>
                    <Route path="/settings/form">
                      {() => (
                        <Redirect
                          to={`/round/${roundId}/admin/proposal-forms`}
                        />
                      )}
                    </Route>
                    <Route path="/settings/importAndExport">
                      {() => (
                        <Redirect to={`/round/${roundId}/admin/data-syncing`} />
                      )}
                    </Route>
                    <Route path="/admin/track-settings/:track">
                      {({ track }) => {
                        if (
                          track.toLowerCase() === "main" ||
                          track.toLowerCase() === "fairness" ||
                          track.toLowerCase() === "freedom"
                        ) {
                          return (
                            <TrackSettings
                              track={
                                track.toUpperCase() as
                                  | "MAIN"
                                  | "FAIRNESS"
                                  | "FREEDOM"
                              }
                            />
                          );
                        }
                        return <div>Invalid track</div>;
                      }}
                    </Route>
                    <Route
                      path="/admin/round-members"
                      component={RoundMembers}
                    />
                    <Route
                      path="/admin/proposal-forms"
                      component={ProposalForms}
                    />
                    <Route path="/admin/data-syncing" component={DataSyncing} />
                    <Route path="/export-data" component={ExportData} />
                    <Route
                      path="/allocations/aggregator"
                      component={Aggregator}
                      nest
                    />
                    <Route path="/allocations/:nodeShortname/:view?" nest>
                      {({ nodeShortname, view }) => {
                        const node = getNodeByShortname(
                          state.round,
                          nodeShortname,
                        );
                        if (
                          node == null ||
                          (node.type !== "funder" &&
                            node.type !== "recommender")
                        ) {
                          return <div>Node not found</div>;
                        }

                        return (
                          <EvaluatorSubpage evaluator={node} view={view} />
                        );
                      }}
                    </Route>
                    <Route path="/settings" component={Settings} />
                    <Route
                      path="/speculation-grants"
                      component={SpeculationGrants}
                    />
                    <Route
                      path="/disagreements"
                      component={DisagreementsPage}
                    />
                    <Route path="/comparisons" component={ComparisonsPage} />
                    <Route
                      path="/logs"
                      component={() => (
                        <Logs
                          round={state.round}
                          lastActionNumber={state.storage.getLastConfirmedVersion()}
                        />
                      )}
                    />
                    <Route path="/onboarding" component={Onboarding} />
                    <Route path="/investigations" component={Investigations} />
                    <Route path="/recusals/:track" component={Recusals} />
                    <Route
                      path="/admin/proposals-viewer"
                      component={ProposalsPage}
                    />
                  </Switch>
                </div>
                <Modal
                  isOpen={proposalInModalId != null}
                  onClose={() => setSearchParams({})}
                  className="w-full max-w-5xl"
                  scrollable={false}
                  fullHeight
                >
                  {proposalInModalId && (
                    <OrgPage evaluableId={proposalInModalId} />
                  )}
                </Modal>
              </div>
            </PinnedNodesContext.Provider>
          </ClientSettingsContext.Provider>
        </DispatchContext.Provider>
      </StateContext.Provider>
    </RefetchUserProfilesContext.Provider>
  );
}

function Banner() {
  const { round, mode, setMode } = useContext(StateContext);
  const [isClearing, setIsClearing] = useState(false);
  const isProdPreview = location.hostname.includes("prod-preview");

  const showBanner =
    mode.viewAsTrackMember != null ||
    mode.viewAsUserId != null ||
    mode.readOnly ||
    isProdPreview;
  if (!showBanner) {
    return null;
  }

  async function clearViewAsTrackMember() {
    setIsClearing(true);
    await sleep(0); // Give up the thread to let the UI update
    void setMode({
      ...mode,
      viewAsTrackMember: null,
    });
    setIsClearing(false);
  }

  async function clearViewAsUserId() {
    setIsClearing(true);
    await sleep(0); // Give up the thread to let the UI update
    void setMode({
      ...mode,
      viewAsUserId: null,
    });
    setIsClearing(false);
  }

  return (
    <div className="bg-blue-500 text-white p-2 flex justify-around">
      <div className="flex items-center gap-2">
        {isProdPreview && <span>🚨 Connected to PROD DB 🚨</span>}
        {mode.readOnly && <span>Read only -- Refresh to continue editing</span>}
        {mode.viewAsTrackMember != null && (
          <>
            <span>Viewing as member of track:</span>
            <span>{trackNames[mode.viewAsTrackMember]}</span>
            {isClearing ? (
              <span>Clearing...</span>
            ) : (
              <button
                className="py-1 cursor-pointer"
                onClick={clearViewAsTrackMember}
              >
                Clear
              </button>
            )}
          </>
        )}
        {mode.viewAsUserId != null && (
          <>
            <span>Viewing as user:</span>
            <span>{getUserShortname(round, mode.viewAsUserId)}</span>
            {isClearing ? (
              <span>Clearing...</span>
            ) : (
              <button
                className="py-1 cursor-pointer"
                onClick={clearViewAsUserId}
              >
                Clear
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
